import React from 'react'

export const useMultiSelect = <T extends { id: string }>(
  items: T[] = [],
  isSelectableItem: (item: T) => boolean,
) => {
  const [isSelectMode, setIsSelectMode] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])

  const selectableItems = React.useMemo(
    () => items.filter(isSelectableItem),
    [items, isSelectableItem],
  )

  const selectableIds = React.useMemo(
    () => selectableItems.map((item) => item.id),
    [selectableItems],
  )

  const isAllSelected = React.useMemo(
    () => {
      const currentSelectableIds = items.filter(isSelectableItem).map((item) => item.id)
      return currentSelectableIds.length > 0 && currentSelectableIds.every((id) => selectedIds.includes(id))
    },
    [items, isSelectableItem, selectedIds],
  )

  const toggleSelectMode = React.useCallback(() => {
    setIsSelectMode((prev) => !prev)
    setSelectedIds([])
  }, [])

  const toggleItem = React.useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((existingId) => existingId !== id) : [...prev, id],
    )
  }, [])

  const toggleAll = React.useCallback(() => {
    setSelectedIds((prev) => {
      const currentSelectableIds = items.filter(isSelectableItem).map((item) => item.id)
      return prev.length === currentSelectableIds.length ? [] : [...currentSelectableIds]
    })
  }, [items, isSelectableItem])

  const clearSelection = React.useCallback(() => {
    setSelectedIds([])
    setIsSelectMode(false)
  }, [])

  return {
    isSelectMode,
    selectedIds,
    selectedCount: selectedIds.length,
    isAllSelected,
    selectableCount: selectableItems.length,
    toggleSelectMode,
    toggleItem,
    toggleAll,
    clearSelection,
  }
}
